// HABITUS translations.
// English (en) and Spanish (es) are complete.
// French (fr), Mandarin (zh), Russian (ru), Arabic (ar) provide key strings;
// any missing keys gracefully fall back to English via deepMerge in LanguageContext.

export const en = {
  meta: { dir: "ltr" },
  nav: {
    concept: "Concept",
    problem: "Problem",
    solution: "Solution",
    layers: "System",
    membership: "Membership",
    waitlist: "Preregister",
    requestInvitation: "Request Invitation",
    joinWaitlist: "Join Waiting List",
    admin: "Admin",
  },
  hero: {
    overline: "Private Operating Environment",
    title: "The infrastructure of global living.",
    subtitle:
      "A private operating environment for globally mobile individuals, families, and organizations.",
    body:
      "HABITUS coordinates communication, protection, readiness, and access so your life, family, and operations remain supported wherever you are.",
    ctaPrimary: "Request Invitation",
    ctaSecondary: "Join Waiting List",
  },
  isNot: {
    overline: "Positioning",
    title: "Not a travel service. Not a concierge. Not a perks program.",
    items: [
      { k: "Benefits cards", v: "Membership cards give benefits and discounts." },
      { k: "Travel optimization", v: "Travel and miles services optimize itineraries and points." },
      { k: "Concierge", v: "Concierge services provide assistance on request." },
      { k: "Isolated coverage", v: "Insurance and rescue memberships remain isolated products." },
    ],
    conclusion:
      "HABITUS is different because it coordinates the full operating environment, not a single benefit.",
  },
  problem: {
    overline: "The Problem",
    title: "Global life still runs on fragmented systems.",
    intro:
      "Mobility has advanced. The systems that support it have not. The result is exposure.",
    points: [
      "Multiple numbers and disconnected identities",
      "Disconnected providers acting in isolation",
      "Reactive response instead of readiness",
      "No single access layer",
      "Family and corporate exposure",
      "No unified readiness system",
    ],
  },
  solution: {
    overline: "The Solution",
    title: "One coordinated operating environment.",
    body:
      "HABITUS connects independent services into one access layer for communication, security, evacuation, protection, access, and family continuity.",
  },
  diagram: {
    overline: "System Architecture",
    title: "How HABITUS is structured.",
    member: "Member",
    habitus: "HABITUS",
    note:
      "A single access point. The member connects to HABITUS, which coordinates eight operating layers.",
  },
  layers: {
    overline: "Product Layers",
    title: "Eight coordinated layers.",
    items: [
      { n: "01", t: "Communication", d: "Permanent global contact channel and identity." },
      { n: "02", t: "Security", d: "Stabilization and crisis coordination." },
      { n: "03", t: "Evacuation / Rescue", d: "Emergency extraction and medical or disaster evacuation routing." },
      { n: "04", t: "Protection", d: "Travel and risk coverage." },
      { n: "05", t: "Access", d: "Trusted places, private clubs, mobility access, and soft-landing environments." },
      { n: "06", t: "Financial Mobility", d: "Payment and liquidity support." },
      { n: "07", t: "Family / Inner Circle", d: "Extend the umbrella to spouse, children, key people, or corporate teams." },
      { n: "08", t: "Operations", d: "One access point coordinating the system." },
    ],
  },
  changes: {
    overline: "How Life Changes",
    title: "Peace of mind is the result of systems already being in place.",
    items: [
      { t: "Clarity", d: "One access point instead of fragmented providers." },
      { t: "Readiness", d: "No improvisation. The system is prepared before it is needed." },
      { t: "Continuity", d: "Life and operations remain coordinated across jurisdictions." },
      { t: "Extension", d: "Family and inner circle are covered under one umbrella." },
    ],
  },
  membership: {
    overline: "Membership",
    title: "Membership by application and review.",
    body:
      "HABITUS is intended for people whose personal, family, or business lives move across jurisdictions.",
    types: [
      { t: "Individual", d: "For the globally mobile principal." },
      { t: "Family", d: "Spouse, children, and household coordinated together." },
      { t: "Corporate / Team", d: "Key people and operating teams under one structure." },
      { t: "Inner Circle", d: "Extended coverage for the people who matter most." },
    ],
    note: "Pricing is disclosed privately during the review process.",
  },
  waitlist: {
    overline: "Preregistration",
    title: "Request preregistration.",
    subtitle:
      "Membership is granted by application and review. Begin by sharing your details below.",
    labels: {
      full_name: "Full name",
      email: "Email",
      phone: "Phone / WhatsApp",
      city: "City",
      country: "Country",
      profile_type: "Profile type",
      interest_type: "Interested in",
      note: "Why is HABITUS relevant to you?",
    },
    placeholders: {
      note: "A short note on your situation and what coordination you are seeking.",
      select: "Select",
    },
    profileOptions: ["Founder", "Investor", "Family Office", "Executive", "Corporate", "Other"],
    interestOptions: ["Individual", "Family", "Corporate / Team", "Inner Circle"],
    submit: "Submit Preregistration",
    submitting: "Submitting...",
    success: "Your preregistration request has been received.",
    successBody: "Our team will review your application and contact you privately.",
    again: "Submit another",
    errorRequired: "Please complete the required fields.",
    errorGeneric: "Something went wrong. Please try again.",
  },
  chatbot: {
    title: "HABITUS Intake",
    open: "Intake Assistant",
    welcome:
      "Welcome to HABITUS. I can help determine whether preregistration is relevant for you.",
    q: [
      "Are you applying as an individual, family, inner circle, or organization?",
      "Which best describes you: founder, investor, family office, executive, corporate, or other?",
      "Which regions do you frequently operate in?",
      "Is your main interest communication, security, evacuation, family coverage, access, or general continuity?",
      "Would you like to request preregistration?",
    ],
    askEmail: "Please share your full name and email so we can follow up.",
    namePrompt: "Your full name",
    emailPrompt: "Your email",
    serious: "Based on your responses, preregistration is relevant. Directing you to the form.",
    early: "Thank you. We will keep you informed as access opens.",
    goForm: "Open Preregistration Form",
    placeholder: "Type your response...",
    send: "Send",
    captured: "Thank you. Your interest has been recorded.",
  },
  footer: {
    tagline: "The infrastructure of global living.",
    rights: "All rights reserved.",
    nav: "Navigation",
    legal: "This site collects preregistration interest only. It is not an offer of services.",
  },
  admin: {
    loginTitle: "Administrator Access",
    loginSubtitle: "HABITUS Operations Console",
    email: "Email",
    password: "Password",
    signIn: "Sign In",
    signingIn: "Signing in...",
    loginError: "Invalid email or password",
    dashboard: "Operations Console",
    logout: "Sign Out",
    total: "Total",
    search: "Search name, email, country...",
    export: "Export CSV",
    sortNewest: "Newest first",
    sortOldest: "Oldest first",
    allStatuses: "All statuses",
    colName: "Name",
    colContact: "Contact",
    colLocation: "Location",
    colProfile: "Profile",
    colInterest: "Interest",
    colStatus: "Status",
    colHubspot: "HubSpot",
    colDate: "Date",
    colNote: "Note",
    empty: "No submissions found.",
    hubspotPending: "Pending",
    hubspotSynced: "Synced",
    hubspotError: "Error",
    hubspotDisabled: "HubSpot not configured — submissions stored in backup database.",
    resyncAll: "Re-sync all to HubSpot",
    resyncing: "Syncing...",
    resyncDone: "Sync complete",
  },
};

export const es = {
  meta: { dir: "ltr" },
  nav: {
    concept: "Concepto",
    problem: "Problema",
    solution: "Solución",
    layers: "Sistema",
    membership: "Membresía",
    waitlist: "Preinscripción",
    requestInvitation: "Solicitar Invitación",
    joinWaitlist: "Unirse a la Lista",
    admin: "Admin",
  },
  hero: {
    overline: "Entorno Operativo Privado",
    title: "La infraestructura de la vida global.",
    subtitle:
      "Un entorno operativo privado para personas, familias y organizaciones globalmente móviles.",
    body:
      "HABITUS coordina comunicación, protección, preparación y acceso para que su vida, su familia y sus operaciones permanezcan respaldadas dondequiera que esté.",
    ctaPrimary: "Solicitar Invitación",
    ctaSecondary: "Unirse a la Lista",
  },
  isNot: {
    overline: "Posicionamiento",
    title: "No es un servicio de viajes. No es un conserje. No es un programa de beneficios.",
    items: [
      { k: "Tarjetas de beneficios", v: "Las tarjetas de membresía otorgan beneficios y descuentos." },
      { k: "Optimización de viajes", v: "Los servicios de viajes y millas optimizan itinerarios y puntos." },
      { k: "Conserjería", v: "Los servicios de conserjería brindan asistencia bajo petición." },
      { k: "Cobertura aislada", v: "Los seguros y las membresías de rescate son productos aislados." },
    ],
    conclusion:
      "HABITUS es diferente porque coordina todo el entorno operativo, no un beneficio aislado.",
  },
  problem: {
    overline: "El Problema",
    title: "La vida global todavía funciona con sistemas fragmentados.",
    intro:
      "La movilidad ha avanzado. Los sistemas que la sustentan no. El resultado es exposición.",
    points: [
      "Múltiples números e identidades desconectadas",
      "Proveedores desconectados que actúan de forma aislada",
      "Respuesta reactiva en lugar de preparación",
      "Sin una única capa de acceso",
      "Exposición familiar y corporativa",
      "Sin un sistema unificado de preparación",
    ],
  },
  solution: {
    overline: "La Solución",
    title: "Un entorno operativo coordinado.",
    body:
      "HABITUS conecta servicios independientes en una sola capa de acceso para comunicación, seguridad, evacuación, protección, acceso y continuidad familiar.",
  },
  diagram: {
    overline: "Arquitectura del Sistema",
    title: "Cómo se estructura HABITUS.",
    member: "Miembro",
    habitus: "HABITUS",
    note:
      "Un único punto de acceso. El miembro se conecta a HABITUS, que coordina ocho capas operativas.",
  },
  layers: {
    overline: "Capas del Producto",
    title: "Ocho capas coordinadas.",
    items: [
      { n: "01", t: "Comunicación", d: "Canal de contacto global permanente e identidad." },
      { n: "02", t: "Seguridad", d: "Estabilización y coordinación de crisis." },
      { n: "03", t: "Evacuación / Rescate", d: "Extracción de emergencia y rutas de evacuación médica o por desastre." },
      { n: "04", t: "Protección", d: "Cobertura de viaje y riesgo." },
      { n: "05", t: "Acceso", d: "Lugares de confianza, clubes privados, acceso a movilidad y entornos de aterrizaje suave." },
      { n: "06", t: "Movilidad Financiera", d: "Soporte de pagos y liquidez." },
      { n: "07", t: "Familia / Círculo Íntimo", d: "Extienda el paraguas a cónyuge, hijos, personas clave o equipos corporativos." },
      { n: "08", t: "Operaciones", d: "Un punto de acceso que coordina el sistema." },
    ],
  },
  changes: {
    overline: "Cómo Cambia la Vida",
    title: "La tranquilidad es el resultado de sistemas que ya están en su lugar.",
    items: [
      { t: "Claridad", d: "Un punto de acceso en lugar de proveedores fragmentados." },
      { t: "Preparación", d: "Sin improvisación. El sistema está listo antes de ser necesario." },
      { t: "Continuidad", d: "La vida y las operaciones permanecen coordinadas entre jurisdicciones." },
      { t: "Extensión", d: "La familia y el círculo íntimo están cubiertos bajo un mismo paraguas." },
    ],
  },
  membership: {
    overline: "Membresía",
    title: "Membresía por solicitud y revisión.",
    body:
      "HABITUS está destinado a personas cuya vida personal, familiar o empresarial se mueve entre jurisdicciones.",
    types: [
      { t: "Individual", d: "Para el principal globalmente móvil." },
      { t: "Familia", d: "Cónyuge, hijos y hogar coordinados juntos." },
      { t: "Corporativo / Equipo", d: "Personas clave y equipos operativos bajo una estructura." },
      { t: "Círculo Íntimo", d: "Cobertura extendida para las personas más importantes." },
    ],
    note: "El precio se revela de forma privada durante el proceso de revisión.",
  },
  waitlist: {
    overline: "Preinscripción",
    title: "Solicitar preinscripción.",
    subtitle:
      "La membresía se otorga por solicitud y revisión. Comience compartiendo sus datos a continuación.",
    labels: {
      full_name: "Nombre completo",
      email: "Correo electrónico",
      phone: "Teléfono / WhatsApp",
      city: "Ciudad",
      country: "País",
      profile_type: "Tipo de perfil",
      interest_type: "Interesado en",
      note: "¿Por qué es relevante HABITUS para usted?",
    },
    placeholders: {
      note: "Una breve nota sobre su situación y la coordinación que busca.",
      select: "Seleccionar",
    },
    profileOptions: ["Fundador", "Inversor", "Family Office", "Ejecutivo", "Corporativo", "Otro"],
    interestOptions: ["Individual", "Familia", "Corporativo / Equipo", "Círculo Íntimo"],
    submit: "Enviar Preinscripción",
    submitting: "Enviando...",
    success: "Su solicitud de preinscripción ha sido recibida.",
    successBody: "Nuestro equipo revisará su solicitud y se pondrá en contacto de forma privada.",
    again: "Enviar otra",
    errorRequired: "Por favor complete los campos obligatorios.",
    errorGeneric: "Algo salió mal. Inténtelo de nuevo.",
  },
  chatbot: {
    title: "Admisión HABITUS",
    open: "Asistente de Admisión",
    welcome:
      "Bienvenido a HABITUS. Puedo ayudar a determinar si la preinscripción es relevante para usted.",
    q: [
      "¿Solicita como individuo, familia, círculo íntimo u organización?",
      "¿Qué le describe mejor: fundador, inversor, family office, ejecutivo, corporativo u otro?",
      "¿En qué regiones opera con frecuencia?",
      "¿Su interés principal es comunicación, seguridad, evacuación, cobertura familiar, acceso o continuidad general?",
      "¿Desea solicitar la preinscripción?",
    ],
    askEmail: "Comparta su nombre completo y correo para poder contactarle.",
    namePrompt: "Su nombre completo",
    emailPrompt: "Su correo electrónico",
    serious: "Según sus respuestas, la preinscripción es relevante. Le dirijo al formulario.",
    early: "Gracias. Le mantendremos informado a medida que se abra el acceso.",
    goForm: "Abrir Formulario de Preinscripción",
    placeholder: "Escriba su respuesta...",
    send: "Enviar",
    captured: "Gracias. Su interés ha sido registrado.",
  },
  footer: {
    tagline: "La infraestructura de la vida global.",
    rights: "Todos los derechos reservados.",
    nav: "Navegación",
    legal: "Este sitio solo recopila interés de preinscripción. No es una oferta de servicios.",
  },
  admin: en.admin, // admin console kept in English (internal tool)
};

// --- Partial locales (key strings translated; rest fall back to English) ---
export const fr = {
  meta: { dir: "ltr" },
  nav: {
    concept: "Concept", problem: "Problème", solution: "Solution", layers: "Système",
    membership: "Adhésion", waitlist: "Préinscription",
    requestInvitation: "Demander une Invitation", joinWaitlist: "Rejoindre la Liste", admin: "Admin",
  },
  hero: {
    overline: "Environnement Opérationnel Privé",
    title: "L'infrastructure de la vie globale.",
    subtitle: "Un environnement opérationnel privé pour les individus, familles et organisations mobiles à l'échelle mondiale.",
    body: "HABITUS coordonne la communication, la protection, la préparation et l'accès afin que votre vie, votre famille et vos opérations restent soutenues où que vous soyez.",
    ctaPrimary: "Demander une Invitation", ctaSecondary: "Rejoindre la Liste",
  },
  isNot: {
    overline: "Positionnement",
    title: "Pas un service de voyage. Pas un concierge. Pas un programme d'avantages.",
    items: [
      { k: "Cartes d'avantages", v: "Les cartes de membre offrent des avantages et des réductions." },
      { k: "Optimisation de voyage", v: "Les services de voyage et de miles optimisent les itinéraires et les points." },
      { k: "Conciergerie", v: "Les services de conciergerie fournissent une assistance sur demande." },
      { k: "Couverture isolée", v: "Les assurances et les adhésions de secours restent des produits isolés." },
    ],
    conclusion: "HABITUS est différent car il coordonne l'environnement opérationnel complet, et non un avantage isolé.",
  },
  problem: {
    overline: "Le Problème",
    title: "La vie globale fonctionne encore sur des systèmes fragmentés.",
    intro: "La mobilité a progressé. Les systèmes qui la soutiennent, non. Le résultat est l'exposition.",
    points: [
      "Multiples numéros et identités déconnectées",
      "Prestataires déconnectés agissant isolément",
      "Réponse réactive au lieu de préparation",
      "Aucune couche d'accès unique",
      "Exposition familiale et corporative",
      "Aucun système de préparation unifié",
    ],
  },
  solution: {
    overline: "La Solution",
    title: "Un environnement opérationnel coordonné.",
    body: "HABITUS relie des services indépendants en une seule couche d'accès pour la communication, la sécurité, l'évacuation, la protection, l'accès et la continuité familiale.",
  },
  diagram: {
    overline: "Architecture du Système",
    title: "Comment HABITUS est structuré.",
    member: "Membre", habitus: "HABITUS",
    note: "Un point d'accès unique. Le membre se connecte à HABITUS, qui coordonne huit couches opérationnelles.",
  },
  layers: {
    overline: "Couches du Produit",
    title: "Huit couches coordonnées.",
    items: [
      { n: "01", t: "Communication", d: "Canal de contact mondial permanent et identité." },
      { n: "02", t: "Sécurité", d: "Stabilisation et coordination de crise." },
      { n: "03", t: "Évacuation / Secours", d: "Extraction d'urgence et acheminement d'évacuation médicale ou en cas de catastrophe." },
      { n: "04", t: "Protection", d: "Couverture voyage et risque." },
      { n: "05", t: "Accès", d: "Lieux de confiance, clubs privés, accès à la mobilité et environnements d'atterrissage en douceur." },
      { n: "06", t: "Mobilité Financière", d: "Soutien aux paiements et à la liquidité." },
      { n: "07", t: "Famille / Cercle Intime", d: "Étendez le parapluie au conjoint, aux enfants, aux personnes clés ou aux équipes corporatives." },
      { n: "08", t: "Opérations", d: "Un point d'accès coordonnant le système." },
    ],
  },
  changes: {
    overline: "Comment la Vie Change",
    title: "La tranquillité d'esprit résulte de systèmes déjà en place.",
    items: [
      { t: "Clarté", d: "Un point d'accès au lieu de prestataires fragmentés." },
      { t: "Préparation", d: "Aucune improvisation. Le système est prêt avant d'être nécessaire." },
      { t: "Continuité", d: "La vie et les opérations restent coordonnées entre les juridictions." },
      { t: "Extension", d: "La famille et le cercle intime sont couverts sous un même parapluie." },
    ],
  },
  membership: {
    overline: "Adhésion",
    title: "Adhésion sur candidature et examen.",
    body: "HABITUS s'adresse aux personnes dont la vie personnelle, familiale ou professionnelle se déplace entre les juridictions.",
    types: [
      { t: "Individuel", d: "Pour le principal mobile à l'échelle mondiale." },
      { t: "Famille", d: "Conjoint, enfants et foyer coordonnés ensemble." },
      { t: "Corporatif / Équipe", d: "Personnes clés et équipes opérationnelles sous une même structure." },
      { t: "Cercle Intime", d: "Couverture étendue pour les personnes qui comptent le plus." },
    ],
    note: "Les tarifs sont communiqués en privé lors du processus d'examen.",
  },
  waitlist: {
    overline: "Préinscription",
    title: "Demander une préinscription.",
    subtitle: "L'adhésion est accordée sur candidature et examen. Commencez par partager vos informations ci-dessous.",
    labels: {
      full_name: "Nom complet", email: "E-mail", phone: "Téléphone / WhatsApp",
      city: "Ville", country: "Pays", profile_type: "Type de profil",
      interest_type: "Intéressé par", note: "Pourquoi HABITUS est-il pertinent pour vous ?",
    },
    placeholders: {
      note: "Une brève note sur votre situation et la coordination que vous recherchez.",
      select: "Sélectionner",
    },
    profileOptions: ["Fondateur", "Investisseur", "Family Office", "Cadre", "Corporatif", "Autre"],
    interestOptions: ["Individuel", "Famille", "Corporatif / Équipe", "Cercle Intime"],
    submit: "Envoyer la Préinscription", submitting: "Envoi...",
    success: "Votre demande de préinscription a été reçue.",
    successBody: "Notre équipe examinera votre candidature et vous contactera en privé.",
    again: "Envoyer une autre",
    errorRequired: "Veuillez remplir les champs obligatoires.",
    errorGeneric: "Une erreur s'est produite. Veuillez réessayer.",
  },
  chatbot: {
    title: "Admission HABITUS", open: "Assistant d'Admission",
    welcome: "Bienvenue chez HABITUS. Je peux vous aider à déterminer si la préinscription est pertinente pour vous.",
    q: [
      "Postulez-vous en tant qu'individu, famille, cercle intime ou organisation ?",
      "Qu'est-ce qui vous décrit le mieux : fondateur, investisseur, family office, cadre, corporatif ou autre ?",
      "Dans quelles régions opérez-vous fréquemment ?",
      "Votre intérêt principal est-il la communication, la sécurité, l'évacuation, la couverture familiale, l'accès ou la continuité générale ?",
      "Souhaitez-vous demander une préinscription ?",
    ],
    askEmail: "Veuillez partager votre nom complet et votre e-mail afin que nous puissions vous recontacter.",
    namePrompt: "Votre nom complet", emailPrompt: "Votre e-mail",
    serious: "D'après vos réponses, la préinscription est pertinente. Je vous dirige vers le formulaire.",
    early: "Merci. Nous vous tiendrons informé à mesure que l'accès s'ouvre.",
    goForm: "Ouvrir le Formulaire de Préinscription",
    placeholder: "Tapez votre réponse...", send: "Envoyer",
    captured: "Merci. Votre intérêt a été enregistré.",
  },
  footer: {
    tagline: "L'infrastructure de la vie globale.",
    rights: "Tous droits réservés.", nav: "Navigation",
    legal: "Ce site recueille uniquement l'intérêt de préinscription. Il ne constitue pas une offre de services.",
  },
  admin: en.admin,
};

export const zh = {
  meta: { dir: "ltr" },
  nav: {
    concept: "理念", problem: "问题", solution: "解决方案", layers: "系统",
    membership: "会员", waitlist: "预注册",
    requestInvitation: "申请邀请", joinWaitlist: "加入等候名单", admin: "管理",
  },
  hero: {
    overline: "私人运营环境",
    title: "全球生活的基础设施。",
    subtitle: "为全球流动的个人、家庭和组织打造的私人运营环境。",
    body: "HABITUS 协调通信、保护、准备和接入，让您的生活、家庭和业务无论身在何处都得到支持。",
    ctaPrimary: "申请邀请", ctaSecondary: "加入等候名单",
  },
  isNot: {
    overline: "定位",
    title: "不是旅行服务。不是礼宾。不是优惠计划。",
    items: [
      { k: "权益卡", v: "会员卡提供权益和折扣。" },
      { k: "旅行优化", v: "旅行和里程服务优化行程与积分。" },
      { k: "礼宾服务", v: "礼宾服务按需提供协助。" },
      { k: "孤立的保障", v: "保险和救援会员仍是孤立的产品。" },
    ],
    conclusion: "HABITUS 与众不同，因为它协调的是完整的运营环境，而非单一权益。",
  },
  problem: {
    overline: "问题",
    title: "全球生活仍依赖于碎片化的系统。",
    intro: "流动性已经进步，支撑它的系统却没有。结果就是暴露于风险之中。",
    points: [
      "多个号码与互不关联的身份",
      "各自为政、互不连接的服务商",
      "被动响应而非充分准备",
      "缺乏单一的接入层",
      "家庭与企业层面的风险暴露",
      "缺乏统一的准备体系",
    ],
  },
  solution: {
    overline: "解决方案",
    title: "一个协调统一的运营环境。",
    body: "HABITUS 将各项独立服务连接为单一接入层，涵盖通信、安全、撤离、保护、接入与家庭连续性。",
  },
  diagram: {
    overline: "系统架构",
    title: "HABITUS 的结构。",
    member: "会员", habitus: "HABITUS",
    note: "单一接入点。会员连接至 HABITUS，由其协调八个运营层级。",
  },
  layers: {
    overline: "产品层级",
    title: "八个协调层级。",
    items: [
      { n: "01", t: "通信层", d: "永久的全球联络渠道与身份。" },
      { n: "02", t: "安全层", d: "局势稳定与危机协调。" },
      { n: "03", t: "撤离 / 救援层", d: "紧急撤离及医疗或灾难疏散路线。" },
      { n: "04", t: "保护层", d: "旅行与风险保障。" },
      { n: "05", t: "接入层", d: "可信场所、私人俱乐部、出行接入与软着陆环境。" },
      { n: "06", t: "金融流动层", d: "支付与流动性支持。" },
      { n: "07", t: "家庭 / 核心圈层", d: "将保护伞延伸至配偶、子女、关键人员或企业团队。" },
      { n: "08", t: "运营层", d: "协调整个系统的单一接入点。" },
    ],
  },
  changes: {
    overline: "生活如何改变",
    title: "内心的安宁源于系统早已就位。",
    items: [
      { t: "清晰", d: "单一接入点，取代碎片化的服务商。" },
      { t: "准备", d: "无需临场应对。系统在需要之前已准备就绪。" },
      { t: "连续", d: "生活与运营在各司法辖区间保持协调。" },
      { t: "延伸", d: "家庭与核心圈被纳入同一保护伞之下。" },
    ],
  },
  membership: {
    overline: "会员",
    title: "通过申请与审核获得会员资格。",
    body: "HABITUS 面向个人、家庭或事业跨越多个司法辖区的人士。",
    types: [
      { t: "个人", d: "面向全球流动的核心人士。" },
      { t: "家庭", d: "配偶、子女与家庭成员统一协调。" },
      { t: "企业 / 团队", d: "关键人员与运营团队归于同一架构。" },
      { t: "核心圈", d: "为最重要的人士提供延伸保障。" },
    ],
    note: "价格将在审核过程中私下披露。",
  },
  waitlist: {
    overline: "预注册",
    title: "申请预注册。",
    subtitle: "会员资格通过申请与审核授予。请先在下方填写您的信息。",
    labels: {
      full_name: "全名", email: "电子邮箱", phone: "电话 / WhatsApp",
      city: "城市", country: "国家", profile_type: "身份类型",
      interest_type: "感兴趣的", note: "HABITUS 为何与您相关？",
    },
    placeholders: { note: "简要说明您的情况以及您希望获得的协调。", select: "选择" },
    profileOptions: ["创始人", "投资者", "家族办公室", "高管", "企业", "其他"],
    interestOptions: ["个人", "家庭", "企业 / 团队", "核心圈"],
    submit: "提交预注册", submitting: "提交中...",
    success: "您的预注册申请已收到。",
    successBody: "我们的团队将审核您的申请并私下与您联系。",
    again: "再次提交",
    errorRequired: "请填写必填字段。",
    errorGeneric: "出现错误，请重试。",
  },
  chatbot: {
    title: "HABITUS 接洽", open: "接洽助理",
    welcome: "欢迎来到 HABITUS。我可以帮助判断预注册是否适合您。",
    q: [
      "您是以个人、家庭、核心圈还是组织的身份申请？",
      "以下哪项最能描述您：创始人、投资者、家族办公室、高管、企业或其他？",
      "您经常在哪些地区开展活动？",
      "您的主要关注点是通信、安全、撤离、家庭保障、接入还是整体连续性？",
      "您是否希望申请预注册？",
    ],
    askEmail: "请提供您的全名和电子邮箱，以便我们跟进。",
    namePrompt: "您的全名", emailPrompt: "您的电子邮箱",
    serious: "根据您的回答，预注册与您相关。正在为您引导至表单。",
    early: "谢谢。随着访问权限开放，我们会及时通知您。",
    goForm: "打开预注册表单",
    placeholder: "输入您的回复...", send: "发送",
    captured: "谢谢。您的意向已记录。",
  },
  footer: {
    tagline: "全球生活的基础设施。",
    rights: "版权所有。", nav: "导航",
    legal: "本网站仅收集预注册意向，不构成任何服务要约。",
  },
  admin: en.admin,
};

export const ru = {
  meta: { dir: "ltr" },
  nav: {
    concept: "Концепция", problem: "Проблема", solution: "Решение", layers: "Система",
    membership: "Членство", waitlist: "Предрегистрация",
    requestInvitation: "Запросить приглашение", joinWaitlist: "В лист ожидания", admin: "Админ",
  },
  hero: {
    overline: "Частная операционная среда",
    title: "Инфраструктура глобальной жизни.",
    subtitle: "Частная операционная среда для глобально мобильных людей, семей и организаций.",
    body: "HABITUS координирует связь, защиту, готовность и доступ, чтобы ваша жизнь, семья и операции оставались под поддержкой, где бы вы ни находились.",
    ctaPrimary: "Запросить приглашение", ctaSecondary: "В лист ожидания",
  },
  isNot: {
    overline: "Позиционирование",
    title: "Не туристический сервис. Не консьерж. Не программа привилегий.",
    items: [
      { k: "Карты привилегий", v: "Клубные карты дают льготы и скидки." },
      { k: "Оптимизация поездок", v: "Сервисы поездок и миль оптимизируют маршруты и баллы." },
      { k: "Консьерж", v: "Консьерж-сервисы оказывают помощь по запросу." },
      { k: "Изолированное покрытие", v: "Страхование и членство в службах спасения остаются отдельными продуктами." },
    ],
    conclusion: "HABITUS отличается тем, что координирует всю операционную среду, а не отдельную привилегию.",
  },
  problem: {
    overline: "Проблема",
    title: "Глобальная жизнь по-прежнему держится на разрозненных системах.",
    intro: "Мобильность продвинулась вперёд. Системы, которые её поддерживают, — нет. Результат — уязвимость.",
    points: [
      "Множество номеров и разрозненных идентичностей",
      "Несвязанные провайдеры, действующие изолированно",
      "Реактивный отклик вместо готовности",
      "Отсутствие единого уровня доступа",
      "Уязвимость семьи и бизнеса",
      "Отсутствие единой системы готовности",
    ],
  },
  solution: {
    overline: "Решение",
    title: "Единая скоординированная операционная среда.",
    body: "HABITUS объединяет независимые сервисы в один уровень доступа для связи, безопасности, эвакуации, защиты, доступа и семейной непрерывности.",
  },
  diagram: {
    overline: "Архитектура системы",
    title: "Как устроен HABITUS.",
    member: "Участник", habitus: "HABITUS",
    note: "Единая точка доступа. Участник подключается к HABITUS, который координирует восемь операционных уровней.",
  },
  layers: {
    overline: "Уровни продукта",
    title: "Восемь скоординированных уровней.",
    items: [
      { n: "01", t: "Связь", d: "Постоянный глобальный канал связи и идентичность." },
      { n: "02", t: "Безопасность", d: "Стабилизация и координация в кризисных ситуациях." },
      { n: "03", t: "Эвакуация / Спасение", d: "Экстренная эвакуация и маршрутизация медицинской эвакуации или при катастрофах." },
      { n: "04", t: "Защита", d: "Покрытие рисков поездок." },
      { n: "05", t: "Доступ", d: "Доверенные места, частные клубы, доступ к мобильности и среда мягкой посадки." },
      { n: "06", t: "Финансовая мобильность", d: "Поддержка платежей и ликвидности." },
      { n: "07", t: "Семья / Ближний круг", d: "Распространение защиты на супруга, детей, ключевых людей или корпоративные команды." },
      { n: "08", t: "Операции", d: "Единая точка доступа, координирующая систему." },
    ],
  },
  changes: {
    overline: "Как меняется жизнь",
    title: "Спокойствие — результат уже выстроенных систем.",
    items: [
      { t: "Ясность", d: "Единая точка доступа вместо разрозненных провайдеров." },
      { t: "Готовность", d: "Никакой импровизации. Система готова до того, как понадобится." },
      { t: "Непрерывность", d: "Жизнь и операции остаются скоординированными между юрисдикциями." },
      { t: "Расширение", d: "Семья и ближний круг защищены под единым зонтом." },
    ],
  },
  membership: {
    overline: "Членство",
    title: "Членство по заявке и рассмотрению.",
    body: "HABITUS предназначен для людей, чья личная, семейная или деловая жизнь перемещается между юрисдикциями.",
    types: [
      { t: "Индивидуальное", d: "Для глобально мобильного основного лица." },
      { t: "Семейное", d: "Супруг, дети и домохозяйство, координируемые вместе." },
      { t: "Корпоративное / Команда", d: "Ключевые люди и операционные команды в единой структуре." },
      { t: "Ближний круг", d: "Расширенное покрытие для самых важных людей." },
    ],
    note: "Стоимость раскрывается конфиденциально в процессе рассмотрения.",
  },
  waitlist: {
    overline: "Предрегистрация",
    title: "Запросить предрегистрацию.",
    subtitle: "Членство предоставляется по заявке и рассмотрению. Начните, поделившись своими данными ниже.",
    labels: {
      full_name: "Полное имя", email: "Эл. почта", phone: "Телефон / WhatsApp",
      city: "Город", country: "Страна", profile_type: "Тип профиля",
      interest_type: "Интересует", note: "Почему HABITUS актуален для вас?",
    },
    placeholders: { note: "Краткая заметка о вашей ситуации и необходимой координации.", select: "Выбрать" },
    profileOptions: ["Основатель", "Инвестор", "Семейный офис", "Руководитель", "Корпорация", "Другое"],
    interestOptions: ["Индивидуальное", "Семейное", "Корпоративное / Команда", "Ближний круг"],
    submit: "Отправить заявку", submitting: "Отправка...",
    success: "Ваша заявка на предрегистрацию получена.",
    successBody: "Наша команда рассмотрит вашу заявку и свяжется с вами конфиденциально.",
    again: "Отправить ещё одну",
    errorRequired: "Пожалуйста, заполните обязательные поля.",
    errorGeneric: "Что-то пошло не так. Попробуйте ещё раз.",
  },
  chatbot: {
    title: "Приём HABITUS", open: "Ассистент приёма",
    welcome: "Добро пожаловать в HABITUS. Я помогу определить, актуальна ли для вас предрегистрация.",
    q: [
      "Вы подаёте заявку как частное лицо, семья, ближний круг или организация?",
      "Что лучше всего вас описывает: основатель, инвестор, семейный офис, руководитель, корпорация или другое?",
      "В каких регионах вы часто работаете?",
      "Ваш основной интерес — связь, безопасность, эвакуация, защита семьи, доступ или общая непрерывность?",
      "Хотели бы вы запросить предрегистрацию?",
    ],
    askEmail: "Поделитесь, пожалуйста, полным именем и эл. почтой, чтобы мы могли связаться.",
    namePrompt: "Ваше полное имя", emailPrompt: "Ваша эл. почта",
    serious: "Судя по вашим ответам, предрегистрация актуальна. Направляю вас к форме.",
    early: "Спасибо. Мы будем держать вас в курсе по мере открытия доступа.",
    goForm: "Открыть форму предрегистрации",
    placeholder: "Введите ваш ответ...", send: "Отправить",
    captured: "Спасибо. Ваш интерес зарегистрирован.",
  },
  footer: {
    tagline: "Инфраструктура глобальной жизни.",
    rights: "Все права защищены.", nav: "Навигация",
    legal: "Этот сайт собирает только интерес к предрегистрации. Это не является предложением услуг.",
  },
  admin: en.admin,
};

export const ar = {
  meta: { dir: "rtl" },
  nav: {
    concept: "المفهوم", problem: "المشكلة", solution: "الحل", layers: "النظام",
    membership: "العضوية", waitlist: "التسجيل المسبق",
    requestInvitation: "طلب دعوة", joinWaitlist: "الانضمام لقائمة الانتظار", admin: "الإدارة",
  },
  hero: {
    overline: "بيئة تشغيل خاصة",
    title: "البنية التحتية للحياة العالمية.",
    subtitle: "بيئة تشغيل خاصة للأفراد والعائلات والمنظمات المتنقلة عالميًا.",
    body: "تنسّق HABITUS الاتصال والحماية والجاهزية والوصول حتى تبقى حياتك وعائلتك وأعمالك مدعومة أينما كنت.",
    ctaPrimary: "طلب دعوة", ctaSecondary: "الانضمام لقائمة الانتظار",
  },
  isNot: {
    overline: "التموضع",
    title: "ليست خدمة سفر. ليست خدمة كونسيرج. ليست برنامج مزايا.",
    items: [
      { k: "بطاقات المزايا", v: "بطاقات العضوية تمنح مزايا وخصومات." },
      { k: "تحسين السفر", v: "خدمات السفر والأميال تحسّن المسارات والنقاط." },
      { k: "الكونسيرج", v: "خدمات الكونسيرج تقدّم المساعدة عند الطلب." },
      { k: "تغطية منعزلة", v: "التأمين وعضويات الإنقاذ تبقى منتجات منعزلة." },
    ],
    conclusion: "تختلف HABITUS لأنها تنسّق بيئة التشغيل الكاملة، وليست مجرد ميزة منفردة.",
  },
  problem: {
    overline: "المشكلة",
    title: "ما زالت الحياة العالمية تعتمد على أنظمة مجزأة.",
    intro: "تقدّمت القدرة على التنقل، لكن الأنظمة التي تدعمها لم تتقدّم. والنتيجة هي الانكشاف.",
    points: [
      "أرقام متعددة وهويات غير مترابطة",
      "مزوّدون منفصلون يعملون بمعزل عن بعضهم",
      "استجابة تفاعلية بدلًا من الجاهزية",
      "غياب طبقة وصول موحّدة",
      "انكشاف على مستوى العائلة والشركة",
      "غياب نظام جاهزية موحّد",
    ],
  },
  solution: {
    overline: "الحل",
    title: "بيئة تشغيل واحدة منسّقة.",
    body: "تربط HABITUS الخدمات المستقلة في طبقة وصول واحدة للاتصال والأمن والإخلاء والحماية والوصول واستمرارية العائلة.",
  },
  diagram: {
    overline: "هندسة النظام",
    title: "كيف تُبنى HABITUS.",
    member: "العضو", habitus: "HABITUS",
    note: "نقطة وصول واحدة. يتصل العضو بـ HABITUS التي تنسّق ثماني طبقات تشغيلية.",
  },
  layers: {
    overline: "طبقات المنتج",
    title: "ثماني طبقات منسّقة.",
    items: [
      { n: "01", t: "الاتصال", d: "قناة اتصال عالمية دائمة وهوية." },
      { n: "02", t: "الأمن", d: "تحقيق الاستقرار وتنسيق الأزمات." },
      { n: "03", t: "الإخلاء / الإنقاذ", d: "الإخلاء الطارئ وتوجيه الإجلاء الطبي أو عند الكوارث." },
      { n: "04", t: "الحماية", d: "تغطية السفر والمخاطر." },
      { n: "05", t: "الوصول", d: "أماكن موثوقة، أندية خاصة، وصول للتنقل وبيئات هبوط سلس." },
      { n: "06", t: "المرونة المالية", d: "دعم المدفوعات والسيولة." },
      { n: "07", t: "العائلة / الدائرة المقرّبة", d: "توسيع المظلة لتشمل الزوج والأبناء والأشخاص الرئيسيين أو فرق الشركة." },
      { n: "08", t: "العمليات", d: "نقطة وصول واحدة تنسّق النظام." },
    ],
  },
  changes: {
    overline: "كيف تتغيّر الحياة",
    title: "راحة البال نتيجة لأنظمة موجودة بالفعل.",
    items: [
      { t: "الوضوح", d: "نقطة وصول واحدة بدلًا من مزوّدين مجزّئين." },
      { t: "الجاهزية", d: "لا ارتجال. النظام جاهز قبل الحاجة إليه." },
      { t: "الاستمرارية", d: "تبقى الحياة والعمليات منسّقة عبر الولايات القضائية." },
      { t: "التوسيع", d: "العائلة والدائرة المقرّبة مشمولتان تحت مظلة واحدة." },
    ],
  },
  membership: {
    overline: "العضوية",
    title: "العضوية بالتقديم والمراجعة.",
    body: "HABITUS موجّهة للأشخاص الذين تتنقّل حياتهم الشخصية أو العائلية أو التجارية عبر الولايات القضائية.",
    types: [
      { t: "فردية", d: "للشخص الرئيسي المتنقّل عالميًا." },
      { t: "عائلية", d: "الزوج والأبناء والأسرة منسّقون معًا." },
      { t: "شركات / فريق", d: "الأشخاص الرئيسيون وفرق التشغيل ضمن هيكل واحد." },
      { t: "الدائرة المقرّبة", d: "تغطية موسّعة للأشخاص الأكثر أهمية." },
    ],
    note: "يُكشف عن الأسعار بشكل خاص أثناء عملية المراجعة.",
  },
  waitlist: {
    overline: "التسجيل المسبق",
    title: "طلب التسجيل المسبق.",
    subtitle: "تُمنح العضوية بالتقديم والمراجعة. ابدأ بمشاركة بياناتك أدناه.",
    labels: {
      full_name: "الاسم الكامل", email: "البريد الإلكتروني", phone: "الهاتف / واتساب",
      city: "المدينة", country: "الدولة", profile_type: "نوع الملف",
      interest_type: "مهتم بـ", note: "لماذا تُعدّ HABITUS مناسبة لك؟",
    },
    placeholders: { note: "ملاحظة موجزة عن وضعك والتنسيق الذي تبحث عنه.", select: "اختر" },
    profileOptions: ["مؤسّس", "مستثمر", "مكتب عائلي", "تنفيذي", "شركة", "أخرى"],
    interestOptions: ["فردية", "عائلية", "شركات / فريق", "الدائرة المقرّبة"],
    submit: "إرسال التسجيل المسبق", submitting: "جارٍ الإرسال...",
    success: "تم استلام طلب التسجيل المسبق الخاص بك.",
    successBody: "سيراجع فريقنا طلبك ويتواصل معك بشكل خاص.",
    again: "إرسال طلب آخر",
    errorRequired: "يرجى إكمال الحقول المطلوبة.",
    errorGeneric: "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
  },
  chatbot: {
    title: "استقبال HABITUS", open: "مساعد الاستقبال",
    welcome: "مرحبًا بك في HABITUS. يمكنني مساعدتك في تحديد ما إذا كان التسجيل المسبق مناسبًا لك.",
    q: [
      "هل تتقدّم بصفتك فردًا أم عائلة أم دائرة مقرّبة أم منظمة؟",
      "أيٌّ يصفك بشكل أفضل: مؤسّس، مستثمر، مكتب عائلي، تنفيذي، شركة أو أخرى؟",
      "في أي المناطق تعمل بشكل متكرّر؟",
      "هل اهتمامك الأساسي هو الاتصال أم الأمن أم الإخلاء أم تغطية العائلة أم الوصول أم الاستمرارية العامة؟",
      "هل ترغب في طلب التسجيل المسبق؟",
    ],
    askEmail: "يرجى مشاركة اسمك الكامل وبريدك الإلكتروني حتى نتمكّن من المتابعة.",
    namePrompt: "اسمك الكامل", emailPrompt: "بريدك الإلكتروني",
    serious: "بناءً على إجاباتك، التسجيل المسبق مناسب. سأوجّهك إلى النموذج.",
    early: "شكرًا لك. سنبقيك على اطّلاع مع فتح الوصول.",
    goForm: "فتح نموذج التسجيل المسبق",
    placeholder: "اكتب ردّك...", send: "إرسال",
    captured: "شكرًا لك. تم تسجيل اهتمامك.",
  },
  footer: {
    tagline: "البنية التحتية للحياة العالمية.",
    rights: "جميع الحقوق محفوظة.", nav: "التنقّل",
    legal: "يجمع هذا الموقع اهتمام التسجيل المسبق فقط. وهو لا يُعدّ عرضًا للخدمات.",
  },
  admin: en.admin,
};

export const LANGUAGES = [
  { code: "en", label: "EN", name: "English" },
  { code: "es", label: "ES", name: "Español" },
  { code: "fr", label: "FR", name: "Français" },
  { code: "zh", label: "中文", name: "中文" },
  { code: "ru", label: "RU", name: "Русский" },
  { code: "ar", label: "AR", name: "العربية" },
];

export const RAW = { en, es, fr, zh, ru, ar };
